'use client'

import * as React from 'react'
import { ChevronDown, type LucideIcon } from 'lucide-react'
import { cn } from '../../utils'

// Mobile counterparts to TopbarNavLink / TopbarNavDropdown.
// These render as full-width rows with large touch targets, suitable
// for use inside the mobile-nav Drawer that the Topbar opens below `lg`.

// ── MobileNavLink ─────────────────────────────────────────────────────────────

interface MobileNavLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  href: string
  active?: boolean
  icon?: LucideIcon
  children: React.ReactNode
  /** Pass `as={Link}` for client-side routing (Next.js, etc.). */
  as?: React.ElementType
}

function MobileNavLink({
  href, active = false, icon: Icon, children, className, as: Comp = 'a', ...props
}: MobileNavLinkProps) {
  return (
    <Comp
      href={href}
      className={cn(
        'flex items-center gap-2.5 w-full px-3 py-2.5 rounded-md',
        'text-sm transition-colors',
        active
          ? 'bg-primary/10 text-primary font-medium'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground',
        className
      )}
      {...props}
    >
      {Icon && <Icon className="size-4 shrink-0" />}
      <span className="truncate">{children}</span>
    </Comp>
  )
}

// ── MobileNavGroup (collapsible) ──────────────────────────────────────────────

interface MobileNavGroupItem {
  href: string
  label: string
  icon?: LucideIcon
  active?: boolean
  as?: React.ElementType
}

interface MobileNavGroupProps {
  label: string
  icon?: LucideIcon
  active?: boolean
  /** Open by default if any child item is active. */
  defaultOpen?: boolean
  items: MobileNavGroupItem[]
  className?: string
}

function MobileNavGroup({
  label, icon: Icon, active = false, defaultOpen, items, className,
}: MobileNavGroupProps) {
  const childActive = items.some((i) => i.active)
  const [open, setOpen] = React.useState<boolean>(defaultOpen ?? childActive ?? false)

  return (
    <div className={cn('flex flex-col', className)}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={cn(
          'flex items-center gap-2.5 w-full px-3 py-2.5 rounded-md',
          'text-sm transition-colors bg-transparent border-none cursor-pointer text-left',
          active || childActive
            ? 'bg-primary/10 text-primary font-medium'
            : 'text-muted-foreground hover:bg-muted hover:text-foreground'
        )}
        aria-expanded={open}
      >
        {Icon && <Icon className="size-4 shrink-0" />}
        <span className="flex-1 truncate">{label}</span>
        <ChevronDown
          className={cn('size-3.5 shrink-0 transition-transform', open && 'rotate-180')}
        />
      </button>
      {open && (
        <div className="flex flex-col gap-0.5 pl-3 pt-1">
          {items.map((item) => {
            const Comp = item.as ?? 'a'
            return (
              <Comp
                key={item.href}
                href={item.href}
                className={cn(
                  'flex items-center gap-2.5 w-full px-3 py-2 rounded-md',
                  'text-[13px] transition-colors',
                  item.active
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                )}
              >
                {item.icon && <item.icon className="size-3.5 shrink-0" />}
                <span className="truncate">{item.label}</span>
              </Comp>
            )
          })}
        </div>
      )}
    </div>
  )
}

export { MobileNavLink, MobileNavGroup }
export type { MobileNavLinkProps, MobileNavGroupProps, MobileNavGroupItem }
